import * as React from "react";
import { Input } from "@/components/ui/Input";
import { DateInput } from "./DateInput";
import { add } from "date-fns";
import { DateInterval, DayPickerProps } from "react-day-picker";
import { Clock4Icon } from "lucide-react";

type DateTimeInputProps = DayPickerProps & {
  value: Date | undefined;
  onUpdate: (value?: Date | undefined) => void;
  placeholder?: string;
  timeZone?: string;
  disabled?: DateInterval | boolean;
  defaultMonth?: Date;
};

export function DateTimeInput({
  value,
  onUpdate,
  placeholder,
  timeZone,
  disabled,
  defaultMonth,
}: DateTimeInputProps) {
  const [date, setDate] = React.useState<Date | undefined>(value);
  const [time, setTime] = React.useState<string | undefined>(
    simpleTimeFromDate(value, timeZone) || "12:00",
  );

  const combineDateTime = (date?: Date, time?: string) => {
    if (!time) {
      return;
    }
    if (!date) {
      return;
    }
    const [hours, minutes, seconds] = time.split(":").map((x) => parseInt(x));
    return add(date, { hours, minutes, seconds });
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <DateInput
          value={date}
          onUpdate={(date) => {
            onUpdate(combineDateTime(date, time));
            setDate(date);
          }}
          placeholder={placeholder}
          timeZone={timeZone}
          disabled={disabled}
          defaultMonth={defaultMonth}
        />
      </div>
      <div className="relative flex w-full items-center gap-2">
        <Clock4Icon className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground select-none" />
        <Input
          type="time"
          id="time-picker"
          step="1"
          data-testid="time-picker"
          onChange={(event) => {
            const newTime = event.target.value;
            onUpdate(combineDateTime(date, newTime));
            setTime(event.target.value);
          }}
          defaultValue={time}
          disabled={typeof disabled === "object" ? undefined : !!disabled}
          min={
            typeof disabled === "object"
              ? simpleTimeFromDate(disabled?.before, timeZone)
              : undefined
          }
          className="appearance-none pl-8 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
        />
      </div>
    </div>
  );
}

function simpleTimeFromDate(date?: Date, timeZone?: string) {
  if (date) {
    // Need to not use AM/PM
    return date.toLocaleTimeString("en-GB", { timeStyle: "short", timeZone });
  }
}
